import PropTypes from 'prop-types';
import { TransformDate } from '../../../components/DatePicker/DatePickerUtils/datePickerUtils';
import { COMPONENT_QUERY, COMPONENT_QUERY_COMPARE } from '../../../constants/api';
import { WEEK_SUBSTRACTION_VALUE } from '../../../constants/datePickerConstants';
import { toTitleCase } from '../../../constants/utils';
import {
  findWeekAndMonth,
  isLeapYear,
  previousWeekExtractor,
} from '../../../data/branchPerformanceData';
import { pivot_config } from '../../../data/overallPerformanceData';
import { calendarActions } from '../../../modules/reducer/calendarSlice';
import { overallPerformanceActions } from '../../../modules/reducer/overallPerformanceSlice';
import api from '../../../services/api';
import { formatNumberUsingUnit } from '../../../utils/formatUtils';
import { OverallPerformanceConstants } from '../../overallPerformance/constant/overallPerformanceConstant';

// Performance Overview Constants
const fiscalMonths = {
  1: 'Dec',
  2: 'Jan',
  3: 'Feb',
  4: 'Mar',
  5: 'Apr',
  6: 'May',
  7: 'Jun',
  8: 'Jul',
  9: 'Aug',
  10: 'Sep',
  11: 'Oct',
  12: 'Nov',
  0: 'Nov', // added this for edge case of last month total
};

export const performanceOverViewTexts = {
  headline: 'Performance Overview',
  branchDropDownLabel: 'Branch',
  oppsSummary: 'Opps Summary',
  ytdSummary: 'YTD Summary',
};

// Cell component for Total column
const TotalCell = ({ cell }) => {
  if (
    cell.row.original.viewData === 'Opportunities Generated' ||
    cell.row.original.viewData === 'Opportunities Closed'
  ) {
    let displayValue;
    if (cell?.value === 'N/A') {
      displayValue = 'N/A';
    } else if (cell?.value === null || cell?.value === 0) {
      displayValue = 0;
    } else {
      displayValue = formatNumberUsingUnit(Math.trunc(cell.value));
    }
    return <span>{displayValue}</span>;
  }
  if (cell.row.original.viewData === 'Close Rate') {
    return <span>{formatNumberUsingUnit(cell.value, '%')}</span>;
  }
  return <span>{formatNumberUsingUnit(cell.value, '$')}</span>;
};

TotalCell.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    row: PropTypes.shape({
      original: PropTypes.shape({
        viewData: PropTypes.string,
      }),
    }),
  }).isRequired,
};

// Cell component for Percent to Plan column
const PercentToPlanCell = ({ cell }) => <span>{formatNumberUsingUnit(cell.value, '%')}</span>;

PercentToPlanCell.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
};

export const columnsArrayYTD = [
  {
    Header: ' ',
    accessor: 'columnName',
    enableRowSpan: true,
  },
  {
    Header: '',
    accessor: 'viewData',
  },
  {
    Header: 'Total',
    accessor: 'total',
    Cell: TotalCell,
  },
  {
    Header: '% to Plan',
    accessor: 'percentToPlan',
    Cell: PercentToPlanCell,
  },
];

export const YTD_KEY_PERF = 'performance_overview_opps_summary_YTD_query';
export const SPEND_KEY_PERF = 'performance_overview_opps_summary_spend_query';
export const HEADER_KEY_PERF = 'performance_overview_header_spends_query';

// performance overview functions

const getFiscalMonthForWeek = (data, weekNumber) => {
  const formattedWeek = `w${weekNumber}`;
  for (const quarterData of data) {
    for (const quarter in quarterData) {
      if (quarterData?.hasOwnProperty(quarter)) {
        const weeks = quarterData[quarter];
        if (weeks?.hasOwnProperty(formattedWeek)) {
          return weeks[formattedWeek]?.fiscal_month;
        }
      }
    }
  }
  return null;
};

export const storeFilters = (
  storeId,
  setCompareYtdFilters,
  setCurrentYtdFilters,
  compareOverallPerformanceRangeYTD,
  overallPerformanceYTDRange
) => {
  const storeFilter = {
    col: 'store_id',
    op: 'in',
    val: [storeId],
  };

  if (!checkForGlobalObject(compareOverallPerformanceRangeYTD)) {
    setCompareYtdFilters([
      {
        col: 'dateindex',
        op: '>=',
        val: compareOverallPerformanceRangeYTD?.start_year
          ?.toString()
          ?.concat(
            compareOverallPerformanceRangeYTD?.start_week <= 9 &&
              compareOverallPerformanceRangeYTD?.start_week > 0
              ? '0'.concat(compareOverallPerformanceRangeYTD?.start_week)
              : compareOverallPerformanceRangeYTD?.start_week
          ),
      },
      {
        col: 'dateindex',
        op: '<=',
        val: compareOverallPerformanceRangeYTD?.end_year
          ?.toString()
          ?.concat(
            compareOverallPerformanceRangeYTD?.end_week <= 9 &&
              compareOverallPerformanceRangeYTD?.end_week > 0
              ? '0'.concat(compareOverallPerformanceRangeYTD?.end_week)
              : compareOverallPerformanceRangeYTD?.end_week
          ),
      },
      storeFilter,
    ]);
  }
  if (!checkForGlobalObject(overallPerformanceYTDRange)) {
    setCurrentYtdFilters([
      {
        col: 'dateindex',
        op: '>=',
        val: overallPerformanceYTDRange?.start_year
          ?.toString()
          ?.concat(
            overallPerformanceYTDRange?.start_week <= 9 &&
              overallPerformanceYTDRange?.start_week > 0
              ? '0'.concat(overallPerformanceYTDRange?.start_week)
              : overallPerformanceYTDRange?.start_week
          ),
      },
      {
        col: 'dateindex',
        op: '<=',
        val: overallPerformanceYTDRange?.end_year
          ?.toString()
          ?.concat(
            overallPerformanceYTDRange?.end_week <= 9 && overallPerformanceYTDRange?.end_week > 0
              ? '0'.concat(overallPerformanceYTDRange?.end_week)
              : overallPerformanceYTDRange?.end_week
          ),
      },
      storeFilter,
    ]);
  }
};

export const configureRowsHandler = (comparedFiltersData) => {
  let data = [];
  if (comparedFiltersData) {
    const measuredResultRows = pivot_config.measuredResult.map((item) => {
      const total =
        comparedFiltersData[item.value]?.current &&
        comparedFiltersData[item.value]?.current !== 'NA'
          ? comparedFiltersData[item.value]?.current?.toFixed(2)
          : 'N/A';

      let percentToPlan = 'N/A';
      if (item.label === 'Spend') {
        percentToPlan =
          comparedFiltersData?.spend_comp_to_plan?.current &&
          comparedFiltersData?.spend_comp_to_plan?.current !== 'NA'
            ? comparedFiltersData?.spend_comp_to_plan?.current?.toFixed(2)
            : 'N/A';
      } else if (item.label === 'Opportunities Generated') {
        percentToPlan =
          comparedFiltersData?.opps_comp_to_plan?.current &&
          comparedFiltersData?.opps_comp_to_plan?.current !== 'NA'
            ? comparedFiltersData?.opps_comp_to_plan?.current?.toFixed(2)
            : 'N/A';
      }

      const percentToPrevYear =
        comparedFiltersData[item.value]?.compare_perc &&
        comparedFiltersData[item.value]?.compare_perc !== 'NA'
          ? comparedFiltersData[item.value]?.compare_perc
          : 'N/A';

      return {
        columnName: 'Measured Result',
        viewData: item.label,
        total,
        percentToPlan,
        percentToPrevYear,
      };
    });

    const calculatedPerformanceRows = pivot_config.calculatedPerformanceIndicators.map((item) => {
      const total =
        comparedFiltersData[item.value]?.current &&
        comparedFiltersData[item.value]?.current !== 'NA'
          ? (() => {
              const fixedValue = comparedFiltersData[item.value]?.current?.toFixed(2);
              return item.label === 'Cancellation Rate' ? `${fixedValue}%` : fixedValue;
            })()
          : 'N/A';

      const percentToPlan =
        item.label === 'Cost per Opportunity' &&
        comparedFiltersData?.cpo_comp_to_plan?.current &&
        comparedFiltersData?.cpo_comp_to_plan?.current !== 'NA'
          ? (() => {
              const fixedValue = comparedFiltersData?.cpo_comp_to_plan?.current?.toFixed(2);
              return item.label === 'Cancellation Rate' ? `${fixedValue}%` : fixedValue;
            })()
          : 'N/A';

      const percentToPrevYear =
        comparedFiltersData[item.value]?.compare_perc &&
        comparedFiltersData[item.value]?.compare_perc !== 'NA'
          ? (() => {
              const compareValue = comparedFiltersData[item.value]?.compare_perc;
              return item.label === 'Cancellation Rate' ? `${compareValue}%` : compareValue;
            })()
          : 'N/A';

      return {
        columnName: ' Calculated Performance Indicators',
        viewData: item.label,
        total,
        percentToPlan,
        percentToPrevYear,
      };
    });

    data = {
      store_id: comparedFiltersData?.store_id || '',
      region_id: comparedFiltersData?.region_id || '',
      rows: [...measuredResultRows, ...calculatedPerformanceRows],
    };
  }
  return data;
};

export const checkForGlobalObject = (object) => {
  if (object === null || object === undefined) {
    return false; // Return false if object is null or undefined
  }
  return (
    Object.values(object).every((value) => value === null || value === undefined) ||
    Object.values(object).some((value) => value === null || value === undefined)
  );
};

export const timePeriodRangeHandler = (
  overallPerformanceRange,
  transformedDataWithDate,
  transformedData,
  dispatch
) => {
  const allValuesAreNull = checkForGlobalObject(overallPerformanceRange);
  const obj = TransformDate?.getCurrentYearAndQuarter(transformedDataWithDate);
  if (allValuesAreNull && obj && Object.keys(obj).length && transformedDataWithDate?.length > 0) {
    const quarterValue =
      transformedDataWithDate?.length &&
      TransformDate?.quickFilters?.TWELVE_WEEK_DATA(
        transformedDataWithDate,
        previousWeekExtractor,
        WEEK_SUBSTRACTION_VALUE
      );
    dispatch(overallPerformanceActions.setOverallPerformanceRange(quarterValue));
  }
};

export const timePeriodYTDRange = (transformedDataWithDate, dispatch) => {
  if (transformedDataWithDate?.length > 0) {
    const ytd = TransformDate.quickFilters.YEAR_TO_DATE(
      transformedDataWithDate,
      previousWeekExtractor
    );
    dispatch(overallPerformanceActions.setOverallPerformanceYTDRange(ytd));
  }
};

export const comparisonPeriodRangeHandler = (
  overallPerformanceRange,
  dispatch,
  setCompareFilter
) => {
  const allValuesAreNullForCurr = checkForGlobalObject(overallPerformanceRange);
  if (!allValuesAreNullForCurr) {
    const range = TransformDate.comparisonPeriod.previousFilter(overallPerformanceRange);
    const value = `w${range?.start_week}, ${range?.start_year} - w${range?.end_week}, ${range?.end_year}`;
    setCompareFilter(value);
    dispatch(overallPerformanceActions.setCompareOverallPerformanceRangeForTableOne(range));
  }
};

export const comparisonPeriodYTD = (overallPerformanceYTDRange, dispatch) => {
  const allValuesAreNull = checkForGlobalObject(overallPerformanceYTDRange);
  !allValuesAreNull &&
    dispatch(
      overallPerformanceActions.setCompareOverallPerformanceRangeYTD(
        TransformDate.comparisonPeriod.previousPeriod(overallPerformanceYTDRange)
      )
    );
};

export const quarterAndYearValueHandler = (transformedDataWithDate, dispatch) => {
  const lastQuarterValue = TransformDate.getLastQuarter(transformedDataWithDate);
  dispatch(overallPerformanceActions.setCurrentQuarter(lastQuarterValue));
  dispatch(calendarActions.setCurrentQuarter(lastQuarterValue));
  const obj = TransformDate?.getCurrentYearAndQuarter(transformedDataWithDate);
  if (obj && Object.keys(obj).length) {
    if (obj.quarter === 'Q1') {
      dispatch(overallPerformanceActions.setCurrentYear(obj.year - 1));
      dispatch(calendarActions.setCurrentYear(obj.year - 1));
    } else {
      dispatch(overallPerformanceActions.setCurrentYear(obj.year));
      dispatch(calendarActions.setCurrentYear(obj.year - 1));
    }
  }
};

export const compareQuarterAndYearValueHandlerForTableOne = (
  overallPerformanceRange,
  transformedDataWithDate,
  transformedData,
  dispatch
) => {
  const allValuesAreNullForCurr = checkForGlobalObject(overallPerformanceRange);
  const obj = TransformDate?.getCurrentYearAndQuarter(transformedDataWithDate);
  if (!allValuesAreNullForCurr && obj && Object.keys(obj).length) {
    const year = obj.year - 1;
    const week = overallPerformanceRange?.start_week;
    const previousValue =
      transformedData.length && TransformDate.findQuarter(year, week, transformedData);
    dispatch(overallPerformanceActions.setCompareSelectedQuarter(previousValue));
    dispatch(calendarActions.setComparisonQuarter(previousValue));
    if (obj.quarter === 'Q1') {
      dispatch(overallPerformanceActions.setCompareSelectedYear(year - 1));
      dispatch(calendarActions.setComparisonYear(year - 1));
    } else {
      dispatch(overallPerformanceActions.setCompareSelectedYear(year));
      dispatch(calendarActions.setComparisonYear(year - 1));
    }
  }
};

const typeOfTable = {
  opps_data: ['actual_opps', 'planned_opps'],
};

// function to get the total of fiscal month for particular store
function getTotalOppsForMonth(data, fiscalMonth, store) {
  return data
    .filter((entry) => entry.fiscal_month === fiscalMonth && entry?.store_id === store)
    .reduce((result, entry) => {
      const yearKey = `${entry.fiscal_year}`;
      result[yearKey] = result[yearKey] || { actual_opps: 0, planned_opps: 0 };
      result[yearKey].actual_opps += entry.actual_opps;
      result[yearKey].planned_opps += entry.planned_opps;
      return result;
    }, {});
}

const findMonthByWeekYear = (data, year, week) => {
  for (const item of data) {
    const currYear = item.currYear;
    if (currYear === year) {
      for (const quarter in item) {
        const quarterData = item[quarter];
        const weekKey = `w${week}`;
        if (quarterData?.hasOwnProperty(weekKey)) {
          return quarterData[weekKey]?.fiscal_month;
        }
      }
    }
  }
  return null;
};

// TO find the month and year value for table one according to data
const monthYearValueFun = (currentMonthValue, currentMonth, currentYear) => {
  // if month is 1 and value is zero updating the month and year value
  if (currentMonthValue === 0 && currentMonth === 1) {
    return { month: 12, year: currentYear - 1 };
  } else if (currentMonthValue === 0) {
    return { month: currentMonth - 1, year: currentYear };
  } else {
    return { month: currentMonth, year: currentYear };
  }
};

// Helper: Check if week is in range based on filter operator
const isWeekInRange = (fiscalWeek, previousWeekValue, currentWeek, filterOperatorBoolean) => {
  return filterOperatorBoolean
    ? fiscalWeek >= previousWeekValue || fiscalWeek <= currentWeek
    : fiscalWeek >= previousWeekValue && fiscalWeek <= currentWeek;
};

// Helper: Check if data key should be included for opps_data type
const shouldIncludeDataKey = (dataKey, currentYear, type) => {
  if (type !== 'opps_data') return false;

  return (
    dataKey.includes(`${currentYear}_actual`) ||
    dataKey.includes(`${currentYear}_planned`) ||
    (!dataKey.includes(`${currentYear}`) && !dataKey.includes(`planned_opps`))
  );
};

// Helper: Get month total value based on data key type
const getMonthTotalValue = (dataKey, previousFiscalMonthTotal, fiscalYear) => {
  return dataKey?.includes('actual')
    ? previousFiscalMonthTotal[fiscalYear]?.actual_opps || 0
    : previousFiscalMonthTotal[fiscalYear]?.planned_opps || 0;
};

// Helper: Process entry and add to segregated data
const processEntry = (entry, key, segregatedData, context) => {
  const {
    monthIndex,
    previousFiscalMonthTotal,
    currentWeek,
    previousWeekValue,
    filterOperatorBoolean,
  } = context;
  const dataKey = `${entry.fiscal_year}_${key}`;
  const weekKey = `${fiscalMonths[entry?.fiscal_month]}_W${entry?.fiscal_week}`;

  if (!isWeekInRange(entry?.fiscal_week, previousWeekValue, currentWeek, filterOperatorBoolean)) {
    return;
  }

  if (monthIndex === entry?.fiscal_month) {
    segregatedData[dataKey][`${fiscalMonths[entry?.fiscal_month]}_Total`] = getMonthTotalValue(
      dataKey,
      previousFiscalMonthTotal,
      entry?.fiscal_year
    );
  }

  segregatedData[dataKey][weekKey] = entry[key] !== null ? entry[key] : 0;
};

// Helper: Initialize data structure for opps data
const initializeSegregatedData = (dataKey, currentYear, type) => {
  if (!shouldIncludeDataKey(dataKey, currentYear, type)) {
    return null;
  }

  return {
    Weekly_Opps_Seasonality: toTitleCase(dataKey.split('_').join(' ')),
  };
};

// Helper: Process entries for a given key
const processEntriesForKey = (sortArr, key, segregatedData, currentYear, type, context) => {
  for (const entry of sortArr) {
    if (!fiscalMonths[entry.fiscal_month]) continue;

    const dataKey = `${entry.fiscal_year}_${key}`;

    if (!segregatedData[dataKey]) {
      const initialData = initializeSegregatedData(dataKey, currentYear, type);
      if (initialData) {
        segregatedData[dataKey] = initialData;
      }
    }

    if (segregatedData[dataKey]) {
      processEntry(entry, key, segregatedData, context);
    }
  }
};

export function transformData(data, type, store, transformedDataWithDate) {
  const currentYear = transformedDataWithDate[0]?.currYear;
  let currentWeek = previousWeekExtractor(transformedDataWithDate, 0)?.subtractedWeekValue;

  const previousWeekDataValue = data?.performance_overview_opps_summary_spend_query?.result?.filter(
    (item) => item.fiscal_year === currentYear && item.fiscal_week === currentWeek
  )?.[0]?.actual_opps;

  currentWeek =
    previousWeekDataValue === 0
      ? previousWeekExtractor(transformedDataWithDate, 0)?.subtractedWeekValue
      : currentWeek;

  const previousWeekValue = previousWeekExtractor(transformedDataWithDate, 4)?.subtractedWeekValue;
  const ifFirstFiscalWeek = previousWeekExtractor(transformedDataWithDate, 0)?.subtractedWeekValue;
  const filterOperatorBoolean = ifFirstFiscalWeek === 1 || previousWeekValue > currentWeek;
  const yearValue = filterOperatorBoolean ? currentYear - 1 : currentYear;
  const monthIndex = findMonthByWeekYear(transformedDataWithDate, yearValue, previousWeekValue);

  const yearFilterdata = data?.performance_overview_opps_summary_spend_query?.result?.filter(
    (data) => data?.fiscal_year >= currentYear - 1
  );

  const previousFiscalMonthTotal = getTotalOppsForMonth(yearFilterdata, monthIndex, store);
  const filteredData = yearFilterdata?.filter((data) => data.store_id === store);

  if (!filteredData.length) return [];

  const segregatedData = {};
  const temp_key = typeOfTable[type];
  const sortArr = filteredData.slice().sort((a, b) => a.fiscal_week - b.fiscal_week);

  const context = {
    monthIndex,
    previousFiscalMonthTotal,
    currentWeek,
    previousWeekValue,
    filterOperatorBoolean,
  };

  for (const key of temp_key) {
    processEntriesForKey(sortArr, key, segregatedData, currentYear, type, context);
  }

  return Object.values(segregatedData);
}

//  for modifying the headers of the performance overview and handling of edge case when current week is < previous week
function modifyHeader(
  headerNameArr,
  currentWeekandMonthObj,
  currentWeek,
  previousWeekValue,
  fiscalMonthIndex,
  filterOperatorBoolean
) {
  const newHeader = {
    Header: `${fiscalMonths[fiscalMonthIndex]} Total`,
    accessor: `${fiscalMonths[fiscalMonthIndex]}_Total`,
  };
  const modifiedHeader = (headerNameArr, _currentWeekandMonthObj) => {
    const modifiedHeader = headerNameArr.filter((data1) => {
      const accessorSlice = parseInt(data1?.accessor?.slice(5)) || 0;
      return filterOperatorBoolean
        ? accessorSlice >= previousWeekValue || accessorSlice <= currentWeek
        : accessorSlice <= currentWeek && accessorSlice >= previousWeekValue;
    });
    return modifiedHeader;
  };

  if (filterOperatorBoolean) {
    const data = modifiedHeader(headerNameArr, currentWeekandMonthObj);
    const decemberHeaders = data.filter((item) => item.Header.startsWith('Dec'));
    const otherHeaders = data.filter((item) => !item.Header.startsWith('Dec'));
    decemberHeaders.sort((a, b) => {
      const weekA = parseInt(a.Header.split(' ')[1].slice(1));
      const weekB = parseInt(b.Header.split(' ')[1].slice(1));
      return weekA - weekB;
    });

    let totalAdded = false;
    const yearChangeModifiedArray = otherHeaders
      .concat(decemberHeaders)
      .reduce((result, current, index, array) => {
        if (
          index > 0 &&
          current.accessor.split('_')[0] !== array[index - 1].accessor.split('_')[0] &&
          !totalAdded
        ) {
          result.push(newHeader);
          totalAdded = true;
        }
        result.push(current);
        return result;
      }, []);
    return yearChangeModifiedArray;
  } else if (!filterOperatorBoolean) {
    const data = modifiedHeader(headerNameArr, currentWeekandMonthObj);
    let totalAdded = false;
    const modifiedHeaderForSameYear = data?.reduce((result, current, index, array) => {
      if (
        index > 0 &&
        current.accessor.split('_')[0] !== array[index - 1].accessor.split('_')[0] &&
        !totalAdded
      ) {
        result.push(newHeader);
        totalAdded = true;
      }
      result.push(current);
      return result;
    }, []);
    return modifiedHeaderForSameYear;
  }
}

export function transformHeaders(data, store, transformedDataWithDate, weeklyOppsData) {
  const currentWeekandMonthObj = findWeekAndMonth(transformedDataWithDate);
  let currentWeek = previousWeekExtractor(transformedDataWithDate)?.subtractedWeekValue;
  const previousWeekDataValue =
    weeklyOppsData?.performance_overview_opps_summary_spend_query?.result?.filter(
      (item) =>
        item.fiscal_year === transformedDataWithDate[0]?.currYear &&
        item.fiscal_week === currentWeek
    )?.[0]?.actual_opps;
  currentWeek =
    previousWeekDataValue === 0
      ? previousWeekExtractor(transformedDataWithDate, 0)?.subtractedWeekValue
      : currentWeek;
  const previousWeekValue = previousWeekExtractor(transformedDataWithDate, 4)?.subtractedWeekValue;
  const filterOperatorBoolean = previousWeekValue > currentWeek;
  const yearValue = filterOperatorBoolean
    ? transformedDataWithDate[0]?.currYear - 1
    : transformedDataWithDate[0]?.currYear;
  const fiscalMonthIndex = findMonthByWeekYear(
    transformedDataWithDate,
    yearValue,
    previousWeekValue
  );
  const dataHeaders = data?.performance_overview_header_spends_query?.result;

  const filteredData = dataHeaders?.filter((data) => data.store_id === store);
  if (!filteredData.length) return [];

  // Determine max weeks based on leap year
  const isLeap = isLeapYear(yearValue);
  const maxWeeks = isLeap ? 53 : 52;

  // Build headers with actual fiscal week numbers from transformedDataWithDate
  let headerNameArr = [];

  // Extract week to month mapping from transformedDataWithDate
  const weekToMonthMap = {};
  transformedDataWithDate.forEach((yearData) => {
    Object.keys(yearData).forEach((key) => {
      if (key.startsWith('Q')) {
        const quarterData = yearData[key];
        Object.keys(quarterData).forEach((weekKey) => {
          if (weekKey.startsWith('w')) {
            const weekNum = parseInt(weekKey.substring(1));
            const fiscalMonth = quarterData[weekKey]?.fiscal_month;
            if (fiscalMonth) {
              weekToMonthMap[weekNum] = fiscalMonth;
            }
          }
        });
      }
    });
  });

  // Generate headers for all weeks up to maxWeeks
  for (let weekNum = 1; weekNum <= maxWeeks; weekNum++) {
    const fiscalMonth = weekToMonthMap[weekNum];
    if (fiscalMonth && fiscalMonths[fiscalMonth]) {
      headerNameArr.push({
        Header: `${fiscalMonths[fiscalMonth]} W${weekNum}`,
        accessor: `${fiscalMonths[fiscalMonth]}_W${weekNum}`,
      });
    }
  }

  return modifyHeader(
    headerNameArr,
    currentWeekandMonthObj,
    currentWeek,
    previousWeekValue,
    fiscalMonthIndex,
    filterOperatorBoolean
  );
}
export const transformOppsHeaders = (oppsSummaryData, transformedDataWithDate, current_year) => {
  let oppsHeaderArray = [];
  const currentWeek = previousWeekExtractor(transformedDataWithDate)?.subtractedWeekValue; // updated as base logic upgraded in function itself to handle previous week and tuesday

  const monthDataExtraction =
    transformedDataWithDate?.[0]?.currYear > current_year
      ? transformedDataWithDate?.[1]
      : transformedDataWithDate?.[0];
  let currentMonth = getFiscalMonthForWeek([monthDataExtraction], currentWeek);
  let currentYear = current_year || transformedDataWithDate?.[0]?.currYear;
  const oppsRowData =
    oppsSummaryData?.data?.performance_overview_opps_summary_YTD_query?.result?.length > 0 &&
    oppsSummaryData?.data?.performance_overview_opps_summary_YTD_query?.result;
  const currentMonthValue = getOppsMonthPer(
    oppsRowData,
    currentYear,
    currentMonth
  )?.opps_comp_to_plan;
  const monthYearValue = monthYearValueFun(currentMonthValue, currentMonth, currentYear);
  currentMonth = monthYearValue?.month;
  currentYear = monthYearValue?.year;
  const previousMonthValue = currentMonth - 1 === 0 ? 12 : currentMonth - 1;
  const yearFilterdata =
    oppsRowData?.length > 0 &&
    oppsRowData?.filter(
      (data) =>
        data?.fiscal_year >= currentYear - 1 &&
        ((data?.fiscal_month === currentMonth && data?.fiscal_year === currentYear) ||
          (data?.fiscal_month === previousMonthValue && data?.fiscal_year === currentYear - 1))
    );
  if (yearFilterdata?.length > 0) {
    yearFilterdata
      ?.sort((a, b) =>
        previousMonthValue !== 12
          ? a.fiscal_month - b.fiscal_month
          : b.fiscal_month - a.fiscal_month
      )
      .filter(
        (eachHeader) => eachHeader.fiscal_month !== null && eachHeader.fiscal_month !== undefined
      )
      .forEach((eachHeader) => {
        for (let i = 1; i < yearFilterdata?.length; i++) {
          oppsHeaderArray.push({
            Header: `${fiscalMonths[eachHeader.fiscal_month]}`,
            accessor: `_${fiscalMonths[eachHeader.fiscal_month]}`,
          });
        }
      });
  }
  oppsHeaderArray?.splice(0, 0, {
    Header: () => <span style={{ width: '120px', display: 'inline-block' }}></span>,
    accessor: '_Heading',
  });
  if (currentMonth >= 2 && currentYear === current_year) {
    // to show YTD in Table only when currentMonth is >= 2
    oppsHeaderArray?.push({ Header: 'YTD', accessor: '_YTD' });
  }
  return oppsHeaderArray;
};

function getOppsMonthPer(data, year, month) {
  const result = data?.find((item) => item.fiscal_year === year && item.fiscal_month === month);
  return result;
}

export const tranformOppsRows = (oppsSummaryData, transformedDataWithDate, current_year) => {
  let transformOppsRowData = [];
  const currentWeek = previousWeekExtractor(transformedDataWithDate, 0)?.subtractedWeekValue; // updated as base logic upgraded in function itself to handle previous week and tuesday
  const monthDataExtraction =
    transformedDataWithDate?.[0]?.currYear > current_year
      ? transformedDataWithDate?.[1]
      : transformedDataWithDate?.[0];
  let currentMonth = getFiscalMonthForWeek([monthDataExtraction], currentWeek);
  let currentYear = current_year || transformedDataWithDate?.[0]?.currYear;
  const yearFilterdata =
    oppsSummaryData?.data?.performance_overview_opps_summary_YTD_query?.result?.length > 0 &&
    oppsSummaryData?.data?.performance_overview_opps_summary_YTD_query?.result?.filter(
      (data) => data?.fiscal_year >= transformedDataWithDate[0]?.currYear - 2
    );
  const currentMonthValue = getOppsMonthPer(
    yearFilterdata,
    currentYear,
    currentMonth
  )?.opps_comp_to_plan;
  const monthYearValue = monthYearValueFun(currentMonthValue, currentMonth, currentYear);
  currentMonth = monthYearValue?.month;
  currentYear = monthYearValue?.year;
  const previousMonthValue = currentMonth - 1 === 0 ? 12 : currentMonth - 1;
  const previousYearValue = previousMonthValue === 12 ? currentYear - 1 : currentYear;
  // not mapped as just two objects data is required
  transformOppsRowData?.push(
    {
      _Heading: '% to Plan',
      [`_${fiscalMonths[previousMonthValue]}`]:
        getOppsMonthPer(yearFilterdata, previousYearValue, previousMonthValue)?.opps_comp_to_plan ||
        'N/A',
      [`_${fiscalMonths[currentMonth]}`]:
        getOppsMonthPer(yearFilterdata, currentYear, currentMonth)?.opps_comp_to_plan || 'N/A',
      _YTD:
        getOppsMonthPer(
          yearFilterdata,
          current_year,
          getFiscalMonthForWeek([monthDataExtraction], currentWeek)
        )?.ytd_opps_to_plan || 'N/A',
    },
    {
      _Heading: '% to Previous Year',
      [`_${fiscalMonths[previousMonthValue]}`]:
        getOppsMonthPer(yearFilterdata, previousYearValue - 1, previousMonthValue)
          ?.opps_comp_to_plan || 'N/A',
      [`_${fiscalMonths[currentMonth]}`]:
        getOppsMonthPer(yearFilterdata, currentYear - 1, currentMonth)?.opps_comp_to_plan || 'N/A',
      _YTD:
        getOppsMonthPer(
          yearFilterdata,
          current_year,
          getFiscalMonthForWeek([monthDataExtraction], currentWeek)
        )?.ytd_opps_to_prev || 'N/A',
    }
  );
  const formattedData = transformOppsRowData?.map((entry) => {
    const formattedEntry = {};
    for (const key in entry) {
      if (typeof entry[key] === 'number') {
        formattedEntry[key] = entry[key]?.toFixed(2) + '%';
      } else {
        formattedEntry[key] = entry[key];
      }
    }
    return formattedEntry;
  });
  return formattedData;
};

// services functions

const getPayload = (key, storeID, filterOne, _filterTwo) => {
  switch (key) {
    case YTD_KEY_PERF:
      return {
        [key]: {
          filters: [
            { col: 'store_id', op: '==', val: storeID },
            { col: 'dateindex', op: '>=', val: filterOne },
          ],
        },
      };

    case SPEND_KEY_PERF:
      return {
        [key]: {
          filters: [
            { col: 'store_id', op: '==', val: storeID },
            { col: 'fiscal_year', op: '>=', val: filterOne },
          ],
        },
      };

    case HEADER_KEY_PERF:
      return { [key]: { filters: [{ col: 'store_id', op: '==', val: storeID }] } };

    default:
      return null;
  }
};

// performance overview services
export async function fetchCurrentToComparisonYearData(data) {
  try {
    const response = await api.post(
      `${COMPONENT_QUERY_COMPARE}?component=${OverallPerformanceConstants.YTD_VIEW_TABLE_DATA}`,
      {
        current_query_builder: {
          filters: data?.currentYtdFilters,
        },
        previous_query_builder: {
          filters: data?.compareYtdFilters,
        },
        join_columns: ['store_id'],
      }
    );
    return response?.data;
  } catch (error) {
    console.error(error);
  }
}

export async function fetchWeeklyOppsData(data) {
  try {
    const response = await api.post(
      COMPONENT_QUERY + `?component=${data?.SPEND_KEY_PERF}`,
      getPayload(data?.SPEND_KEY_PERF, data?.storeId, data?.yearFilter)
    );
    return response?.data;
  } catch (error) {
    console.error(error);
  }
}

export async function fetchColumnHeaders(data) {
  try {
    const response = await api.post(
      COMPONENT_QUERY + `?component=${data?.HEADER_KEY_PERF}`,
      getPayload(data?.HEADER_KEY_PERF, data?.storeId)
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
}

export async function fetchOppsSummary(data) {
  try {
    const response = await api.post(
      COMPONENT_QUERY + `?component=${data?.YTD_KEY_PERF}`,
      getPayload(data?.YTD_KEY_PERF, data?.storeId, data?.oppsFilter)
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
}
